import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy,
  serverTimestamp,
  setDoc
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';

// Collection reference
const projectsCollection = 'projects';

// Create a new project
export const createProject = async (projectData) => {
  try {
    // console.log('Starting project creation...');
    const currentUser = auth.currentUser;
    // console.log('Current user status:', currentUser ? 'Signed in' : 'Not signed in');
    
    // If user is signed in, use their UID
    // If not, create an anonymous project
    const userId = currentUser?.uid || null;
    
    // Generate a new document reference with a unique ID
    const docRef = doc(collection(db, projectsCollection));
    
    // Create the project with the generated ID
    await setDoc(docRef, {
      ...projectData,
      userId,
      isAnonymous: !currentUser,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'active'
    });
    
    // console.log('Project created successfully with ID:', docRef.id);
    return { id: docRef.id, ...projectData };
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

// Associate an anonymous project with a user
export const associateProjectWithUser = async (projectId, userId) => {
  try {
    // console.log(`Associating project ${projectId} with user ${userId}...`);
    
    const docRef = doc(db, projectsCollection, projectId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      // console.error('Project not found:', projectId);
      throw new Error('Project not found');
    }

    const data = docSnap.data();
    if (!data.isAnonymous) {
      // console.error('Project is not anonymous:', projectId);
      throw new Error('Project is not anonymous');
    }

    await updateDoc(docRef, {
      userId,
      isAnonymous: false,
      updatedAt: serverTimestamp()
    });
    
    // console.log('Successfully associated project with user');
    return { id: projectId, userId };
  } catch (error) {
    console.error('Error associating project with user:', error);
    throw error;
  }
};

// Get a single project by ID
export const getProject = async (projectId) => {
  try {
    const docRef = doc(db, projectsCollection, projectId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      const currentUser = auth.currentUser;
      
      // Check if project is public
      if (data.access === 'public') {
        return { id: docSnap.id, ...data };
      }
      
      // For non-public projects, check if user has permission
      if (data.userId === currentUser?.uid || data.isAnonymous) {
        return { id: docSnap.id, ...data };
      } else {
        throw new Error('Unauthorized access');
      }
    } else {
      throw new Error('Project not found');
    }
  } catch (error) {
    console.error('Error getting project:', error);
    throw error;
  }
};

// Get all projects for the current user
export const getUserProjects = async () => {
  try {
    console.log('projectService: Getting user projects');
    const currentUser = auth.currentUser;
    console.log('projectService: Current user:', currentUser ? 'present' : 'null');
    
    if (!currentUser) {
      console.log('projectService: No authenticated user found');
      throw new Error('User must be authenticated');
    }

    console.log('projectService: Building query for user:', currentUser.uid);
    const q = query(
      collection(db, projectsCollection),
      where('isAnonymous', '==', false),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      orderBy('__name__', 'asc')
    );
    
    console.log('projectService: Executing query');
    const querySnapshot = await getDocs(q);
    console.log('projectService: Query returned', querySnapshot.docs.length, 'documents');
    console.log('projectService: Query docs:', querySnapshot);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('projectService: Error getting user projects:', error);
    throw error;
  }
};

// Update a project
export const updateProject = async (projectId, updateData) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    const docRef = doc(db, projectsCollection, projectId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Project not found');
    }

    const data = docSnap.data();
    if (data.userId !== currentUser.uid) {
      throw new Error('Unauthorized access');
    }

    // Ensure title is not undefined
    if (updateData.title === undefined) {
      updateData.title = '';
    }
    await updateDoc(docRef, {
      ...updateData,
      updatedAt: serverTimestamp()
    });
    
    return { id: projectId, ...updateData };
  } catch (error) {
    console.error('Error updating project:', error);
    throw error;
  }
};

// Delete a project
export const deleteProject = async (projectId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated');
    }

    const docRef = doc(db, projectsCollection, projectId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      throw new Error('Project not found');
    }

    const data = docSnap.data();
    if (data.userId !== currentUser.uid) {
      throw new Error('Unauthorized access');
    }

    await deleteDoc(docRef);
    return projectId;
  } catch (error) {
    console.error('Error deleting project:', error);
    throw error;
  }
};

// Updated project schema example:
/*
{
  id: string,              // Auto-generated by Firestore
  userId: string,          // User ID or anonymous ID
  isAnonymous: boolean,    // Whether this is an anonymous project
  title: string,           // Project title
  description: string,     // Project description
  images: [{              // Array of image objects
    url: string,          // GCS URL
    previewUrl: string,   // Local preview URL
    uploadedAt: timestamp
  }],
  settings: {             // Project settings
    checkboxSelections: {  // All checkbox selections from prompts
      [slideIndex: string]: string[]
    },
    radioSelections: {     // All radio selections from prompts
      [slideIndex: string]: string
    },
    sliderValues: {        // All slider values from prompts
      [slideIndex: string]: {
        [sliderIndex: string]: number
      }
    },
    multipleTextValues: {  // All text input values from prompts
      [slideIndex: string]: {
        [inputIndex: string]: string
      }
    }
  },
  status: string,         // active, archived, deleted
  createdAt: timestamp,   // Auto-generated
  updatedAt: timestamp    // Auto-updated
}
*/ 